<script setup lang="ts">
import * as z from "zod";
import { useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import { v4 as uuidv4 } from 'uuid';
import { Loader2, Router } from "lucide-vue-next";
import { useBreakpoints, useFocus } from '@vueuse/core';
// import { vAutoAnimate } from '@formkit/auto-animate'
import { isEqual } from 'lodash-es';
import { useAccessControl } from '@/composables/useAccessControl';
import { useBottomDrawer } from '@/composables/useBottomDrawer'
import { ref, computed, watch, markRaw } from 'vue'
import { Input } from '@/components/ui/input'
import TeamDeleteDialog from '@/components/TeamDeleteDialog.vue'

const client = useSupabaseClient()
const userStore = useUserStore();
const uiStore = useUiStore()

const { setTeams, setSelectedTeam, setUser, setSearchToken } = userStore;
const { teams, selectedTeam, plan, user } = storeToRefs(userStore);

const headers = useRequestHeaders(["cookie"]);
const {toggleDialog} = uiStore

// Add new refs for access control
const { canCreateTeam, isLoading: accessCheckLoading } = useAccessControl()

const inviteUrl = ref('');


const personalTeam = computed(() => {
  return {
    id: user.value?.id,
    metadata: {
      name: user.value?.email || 'Personal',
      type: "Personal",
      description: "Personal account",
    },
  };
});

const sendingInvitations = ref('')

const sendInvitations = async () => {
  sendingInvitations.value = 'pending'
  
  // Extract just the encrypted codes from the invite URLs for email sending
  const inviteCodes = inviteUrls.value?.map(invite => invite.code) || [];
  
  console.log('📧 Sending invitations with codes:', inviteCodes);
  
  // use resend api to send out invites
  const { data, error, status } = await useFetch('/api/invite/send', {
    method: 'POST',
    headers,
    body: {
      team_name: team_name.value,
      invited_users: invitedUsers.value,
      invite_links: inviteCodes,
      user: user.value
    }
  })
  sendingInvitations.value = 'sent'

  if (status.value == 'success') {
    sendingInvitations.value = 'delivered'
  }
  
}

// const { x, y, isScrolling, arrivedState, directions } = useScroll(scrollpane)

// watch(y.value, (newVal, oldVal) => {
//     console.log(newVal, oldVal)
// })

// const user = ref({ display_name: 'Edan Weconst inviteUrl = ref('');is', email: 'edanweis@gmail.com' })

// watch teams
// watchEffect(async () => {
//     if (teams?.value !== userStore?.teams?.value) {
//         setTeams(userStore?.teams?.value);
//     }
// })

// watch the user object for 'subscription_status' change

watch(selectedTeam, (newVal, oldVal) => {
  
})

interface TeamAlertData {
  id: string;
  metadata: {
    name: string;
    archived?: boolean;
    muted?: boolean;
  };
  action: 'delete' | 'leave';
}

const alertData = ref<TeamAlertData>({
  id: '',
  metadata: {
    name: '',
    archived: false,
    muted: false
  },
  action: 'delete'
})

// Replace showAlert with bottomDrawer
const bottomDrawer = useBottomDrawer()

const showAlertMessage = (team: TeamAlertData, action: 'delete' | 'leave') => {
  console.log('Showing alert for team:', team, 'action:', action)
  alertData.value = { ...team, action }
  inputTeamName.value = '' // Reset input field
  
  // Open bottom drawer instead of showing alert
  bottomDrawer.open({
    title: ``,
    description: ``,
    hideCloseButton: false,
    actions: [
      {
        label: 'Cancel',
        action: 'cancel',
        variant: 'ghost'
      },
      {
        label: action === 'delete' ? 'Delete' : 'Leave',
        action: 'confirm',
        variant: action === 'delete' ? 'destructive' : 'default',
        data: team
      }
    ],
    onAction: (action, data) => {
      if (action === 'confirm') {
        if (alertData.value.action === 'delete') {
          deleteTeam(alertData.value)
        } else {
          leaveTeam(alertData.value)
        }
      } else {
        bottomDrawer.close()
      }
    },
          component: markRaw(TeamDeleteDialog),
      props: {
        alertData: alertData,
        inputTeamName: inputTeamName,
        deletePending: deletePending
      },
      listeners: {
        'update:inputTeamName': (value: string) => {
          inputTeamName.value = value
        }
      }
  })
}


const archived = computed(() => [
  {
    teams: teams.value || [], // Assuming teams.value has a 'teams' array
    filter: (team) => team.metadata.archived && team.metadata.provisioned !== false,
    subMenu: [
      { label: {owner:'Restore', member: 'Restore'}, icon: 'lucide:archive-restore', action: (team) => { toggleArchiveTeam(team) } },
      { label: {owner:'Delete', member: 'Leave'}, icon: 'lucide:trash', action: (team) => { leaveOrDeleteTeam(team) } },
    ]
  }
])
const groups = computed(() => [
  {
    label: "Personal Account",  
    teams: [personalTeam.value],
    filter: () => true,
    subMenu: [
      { label: { owner: 'Settings', member: 'Settings' }, icon: 'lucide:settings', action: (team) => { navigateTo('/account'); subMenuActive.value = false; open.value = false } }
    ]
  },
  {
    label: "Teams",
    teams: teams.value || [], // Assuming teams.value has a 'teams' array
    filter: (team) => !team.metadata.archived && team.metadata.provisioned !== false,
    subMenu: [
      { label: { owner: 'Settings', member: 'Settings' }, icon: 'lucide:settings', action: (team) => { navigateTo(`/team/${team.id}/settings`); subMenuActive.value = false; open.value = false } },
      { label: {owner:'Mute', member: 'Mute'}, icon: 'lucide:volume-x', action: (team) => { muteTeam(team) } },
      { label: {owner:'Archive', member: 'Archive'}, icon: 'lucide:archive', action: (team) => {toggleArchiveTeam(team)} },
      { label: { owner: 'Delete', member: 'Leave'}, icon: 'lucide:trash', action: (team) => { leaveOrDeleteTeam(team) } },
    ]
  }
]);

const open = ref(false);
const showNewTeamDialog = ref(false);
const showTeamMenu = ref(false);
const subMenuActive = ref(false);
const currentTab = ref("trial");
const selectedUseCase = ref("");
const inputTeamName = ref("");
const isActive = computed(() => {
  const status = user?.value?.app_metadata?.subscription_status;
  return status === 'active' || status === 'trialing';
});
const isTeamsPlan = computed(() => plan.value?.product?.name == 'Teams');
currentTab.value = isActive.value && isTeamsPlan.value ? 'use' : 'trial'

const submitCallback = ref(null)
const inviteUrls = ref<InviteLink[] | null>(null)


// if (!selectedTeam.value ){
//     setSelectedTeam(groups.value[0].teams[0]);
//   }

const totalTeams = computed(() => {
  return groups.value.reduce((sum, group) => sum + group.teams.length, 0)
})


const resetTab = () => {
  currentTab.value = isActive.value && isTeamsPlan.value ? 'use' : 'trial'
}

const useCases = [
  {
    id: "landscape_architect",
    label: "Landscape Architecture",
    desc: "Faster planting, beautifully presented",
    image: "https://ik.imagekit.io/8qxqs9cznn/general/tr:w-0.5/out-0%20(8).webp"
  },
  {
    id: "nursery",
    label: "Nursery Supply",
    desc: "Better forecasting, quicker quotes",
    image: "https://ik.imagekit.io/8qxqs9cznn/general/tr:w-0.5/out-0%20(10).webp"
  },
  {
    id: "gov_research",
    label: "Gov/Research",
    desc: "Better outcomes, a lighter inbox",
    image: "https://ik.imagekit.io/8qxqs9cznn/general/tr:w-0.5/out-1%20(9).webp"
    
  },

  {
    id: "personal",
    label: "Personal",
    desc: "Refine my planting pallet",
    image: "https://ik.imagekit.io/8qxqs9cznn/general/out-0%20(11).webp"
  },
];

const handleKeydown = (event) => {
  const ignoreKeys = ["a", "A"];
  if (ignoreKeys.includes(event.key)) {
    event.preventDefault(); // Prevents the key action
  } else {
    // event.stopPropagation();
  }
};





const schema = z.object({
  name: z.string().min(1, "Team name is required"),
  desc: z.string().optional(),
  invitedUsers: z.union([
    z.array(z.string().email()),
    z.string().optional().refine(val => val === '', { message: "Invalid value" })
  ]).optional(),
});

const {
  handleSubmit,
  meta,
  setErrors,
  error: formError,
} = useForm({
  validationSchema: toTypedSchema(schema),
});

// const invitedUsers = ref<string[]>([]); // Define as an array of strings
const invitedUsers = ref([]);

const {
  errorMessage,
  value,
  errors: invitedUsersError,
} = useField<string[]>("invitedUsers", {
  initialValue: [],
  label: "Invite users",
  validateOnMount: false,
  syncVModel: true,
});

watch(showNewTeamDialog, (newVal, oldVal) => {
  if (!newVal && oldVal) {
    // newVal is false when the dialog is closed
    resetTab()
    selectedUseCase.value = "";
    sendingInvitations.value = ''
    submitCallback.value = null
  }
  if (newVal) {
    inviteUrl.value = null
    inviteUrls.value = null
    sendingInvitations.value = ''
    submitCallback.value = null
  }
});

// create createTeamPending computed prop
const createTeamPending = computed(() => {
  return createTeamPending.value;
});

// const refreshTeams = async () => {
//   const { data: newUser, refresh } = useFetch('/api/users/user')
//   setUser(newUser.value)
//   const teams = await await fetchUserData(headers).teams();
//   setTeams(teams);
//   commandKey.value++;
// };

const commandKey = ref(0);

const toggleArchiveTeam = async (team) => {
  if (selectedTeam.value == team) {
    setSelectedTeam(groups.value[0].teams[0]);
  }
  
  await updateMetadata(team, {archived: !team?.metadata?.archived})
  
  
};

const updateMetadata = async (team: any, newMetadata: any) => {

  let mergedMetadata = { ...team.metadata, ...newMetadata }
  //update store team
  setTeams(teams?.value.map(t => t.id === team.id ? { ...t, metadata: mergedMetadata } : t)
  );

  
  const { data, error: metadataError } = await client
    .from('groups')
    .select()
    .match({ 'id': team.id });


  const { error: updateMetadataError } = await client
    .from('groups')
    .update({ metadata: mergedMetadata })
    .match({ 'id': team.id })

  if (updateMetadataError) {
    console.log('updateMetadataError', updateMetadataError)
  }
}



const muteTeam = async (team) => {
  await updateMetadata(team, {muted: !team?.metadata?.muted})
};

const checkGroup = ref("");
const isTeamOwner = ref(false);


watch(checkGroup, async (newVal) => {
  isTeamOwner.value = user.value?.app_metadata?.groups?.[newVal]?.includes("owner") || false;
});

const leaveOrDeleteTeam = async (team) => {
  // Add null check for user.value and app_metadata
  const userGroups = user.value?.app_metadata?.groups || {};
  
  const remainingGroups = Object.entries(userGroups)
    .filter(([key, roles]) => roles.includes("owner"))
    .map(([key]) => key);

  // check if team is the only group where they are "owner"
  if (remainingGroups.length == 1) {
    // backup the object
    let t = { teams: [...teams?.value] }

    const { error: updateMetadataError } = await client
      .from("groups")
      .update({ "metadata": { "provisioned": false } })
      .eq("id", team.id)

    if (updateMetadataError) {
      setTeams(t)
    }
    // remove from state
    setTeams([])
  } else {
    // check if owner - add null check here too
    console.log('Checking team ownership:', isTeamOwner.value)
    checkGroup.value = team.id // Make sure we set the checkGroup value to trigger the watch
    
    // Use nextTick to ensure the watch has executed
    await nextTick()
    
    if (isTeamOwner.value) {
      console.log('User is team owner, showing delete dialog')
      showAlertMessage(team, 'delete')
    } else {
      console.log('User is not team owner, showing leave dialog')
      showAlertMessage(team, 'leave')
    }
  }
};


const forceCloseDialog = () => {
  showNewTeamDialog.value = false;
  resetTab();
  selectedUseCase.value = "";
  sendingInvitations.value = '';
  submitCallback.value = null;
  inviteUrls.value = null;
};

const goToTeam = async (id) => {
  try {
    console.log('🚀 Navigating to team:', id);
    
    // Find and set the team (if it exists in current teams)
    const team = teams.value.find(t => t.id == id);
    if (team) {
      setSelectedTeam(team);
      console.log('✅ Selected team set:', team);
    } else {
      console.warn('⚠️ Team not found in teams array:', id);
    }
    
    // Force close dialog and reset state
    forceCloseDialog();
    
    // Navigate to the team page
    await navigateTo({
      path: `/team/${id}`,
      query: {
        'intent': 'onboard'
      }
    });
    
    console.log('✅ Navigation completed');
    
  } catch (error) {
    console.error('❌ Error navigating to team:', error);
    // Ensure dialog closes even if navigation fails
    forceCloseDialog();
  }
};


const deletePending = ref(false);

const leaveTeam = async (team: TeamAlertData) => {
  deletePending.value = true;
  
  try {
    const { data, error } = await client.from('group_users')
      .delete()
      .eq('group_id', team.id)
      .eq('user_id', user.value.id)

    if (error) {
      console.error('Error leaving team:', error);
      push.error({
        title: 'Error',
        message: 'Failed to leave team'
      });
    } else {
      setTeams(teams.value.filter(t => t.id != team.id));
      push.success({
        title: 'Success',
        message: 'You have left the team'
      });
    }
    
  } catch (err) {
    console.error('Error leaving team:', err);
  } finally {
    bottomDrawer.close(); // Close the bottom drawer instead of the alert
    deletePending.value = false;
    inputTeamName.value = '';
  }
}

const deleteTeam = async (team: TeamAlertData) => {
  deletePending.value = true;
  
  try {
    const { data: collections, error: collectionsError } = await client
      .from('asset_collections')
      .delete()
      .eq('group_id', team.id);

    if (collectionsError) {
      console.error('Error deleting collections:', collectionsError);
      push.error({
        title: 'Error',
        message: 'Failed to delete team collections'
      });
      deletePending.value = false;
      return;
    }

    const { data, error } = await useAsyncData('groups', async () => {
      await client.from('group_users').delete().eq('group_id', team.id);
      await client.from('groups').delete().eq('id', team.id);
    });

    if (error.value) {
      console.error('Error deleting team:', error.value);
      push.error({
        title: 'Error',
        message: 'Failed to delete team'
      });
    } else {
      setTeams(teams.value.filter(t => t.id != team.id));
      push.success({
        title: 'Success',
        message: 'Team deleted successfully'
      });
    }
    
  } catch (err) {
    console.error('Error in delete operation:', err);
  } finally {
    bottomDrawer.close(); // Close the bottom drawer instead of the alert
    deletePending.value = false;
    inputTeamName.value = '';
  }
}


const team_name = ref(null)

const onSubmit = handleSubmit(async (values) => {
  currentTab.value = "invite";
  if (!meta.value.valid) {
    setErrors(formError);
    return;
  }

  team_name.value = values.name

  const invited_users = values.invitedUsers ? values.invitedUsers.map(email => ({ id: uuidv4(), email })): []
  try {
    const {
      data,
      pending: createTeamPending,
      error,
    } = await useFetch("/api/groups/create", {
      method: "POST",
      headers,
      body: {
        invited_by: user?.value?.id,
        roles: ["admin"],
        group_name: values.name,
        group_desc: values.desc,
        invited_users,
        use_case: selectedUseCase.value,
        provisioned: true,
      },
    });

    if (error.value) {
      return
    }
    
    submitCallback.value = data.value
    
    // Store the search token
    if (data.value?.search_token) {
      setSearchToken(data.value.search_token);
    }
    
    // Add the newly created team to the teams array
    const newTeam = {
      id: data.value.group_id,
      metadata: {
        name: values.name,
        description: values.desc,
        use_case: selectedUseCase.value,
        provisioned: true
      }
    };
    setTeams([...teams.value, newTeam]);
    
    // Ensure we're setting the inviteUrls ref with the proper data
    inviteUrls.value = data.value?.data?.encrypted?.map((e: string) => ({
      link: `${window.location.origin}/invite/${e}`,
      code: e, // Assuming 'e' is the code
    })) || null;

    push.success({
      title: "Success! Team created",
      description: `${values.name} team created successfully`,
    });

    inviteUrl.value = `${window.location.origin}/invite/${toRaw(data).value.data.id}`
    
  } catch (err) {
    if (err != {}) {
      console.error(err)
      push.error({
        title: "Error creating team",
        description: err.message || "An error occurred",
      });
    }
  }
});


const filterFunction = (groups, term) => {
  if (!term) return groups;
  term = term.toLowerCase();
  return groups
    .slice(0, -1)
    .filter((group) => group.metadata.name.toLowerCase().includes(term));
};

const areStringsEqual = (str1: string, str2: string) => {
  return isEqual([...str1], [...str2]);
};

defineEmits(['close-menu'])

// Update tooltip message to be more specific
const getDisabledMessage = computed(() => {
  if (!user.value?.id) return 'Please sign in first'
  return 'You are currently on the waitlist. Team creation will be enabled once you receive access.'
})
</script>

<template #default>
  <form :key="commandKey" @submit.prevent="onSubmit">
    <Dialog :key="commandKey" v-model:open="showNewTeamDialog">
      <Command :key="commandKey" :class="['min-h-fit flex flex-col justify-between', { 'opacity-100': subMenuActive }]" :filter-function="filterFunction">
        <CommandList :style="{'min-height': `${(162)+ (totalTeams * 32)}px`}" :class="['overflow-y-hidden']">
          <CommandInput placeholder="Search team..." />
          <CommandEmpty>No teams found.</CommandEmpty>
          <!-- <PerfectScrollbar> -->
          <TeamGroups v-model:isTeamOwner="isTeamOwner" v-model:check-group="checkGroup" :groups="groups"
            :selectedTeam="selectedTeam" @close-menu="$emit('close-menu')" />
            <!-- </PerfectScrollbar> -->
          <Accordion v-if="archived[0].teams.filter(e => e.metadata.archived).length > 0" type="single" collapsible
            class="no-chevron">
            <AccordionItem value="item-1">
              <AccordionTrigger>
                <p
                  class="-m-8 text-xs bg-muted rounded p-1 px-2 !no-underline text-muted-foreground font-semibold ml-0 !no-underline w-[95%]">
                  <Icon name="lucide:archive" class="mr-2" />
                  <span class="!no-underline">Archived</span>
                </p>
              </AccordionTrigger>
              <AccordionContent class="rounded">
                <TeamGroups v-model:isTeamOwner="isTeamOwner" v-model:check-group="checkGroup" :groups="archived"
                  :selectedTeam="selectedTeam" class="pb-10" />
              </AccordionContent>
            </AccordionItem>
          </Accordion>


        </CommandList>

        <CommandSeparator />
        
        <CommandList>
          <CommandGroup>
            <DialogTrigger @keydown.stop as-child>
              <CommandItem 
                class="cursor-pointer hover:!bg-muted" 
                :class="{ 'cursor-not-allowed': !canCreateTeam }"
                :disabled="!canCreateTeam"
                value="create-team" 
                @select="() => {
                  open = false;
                  showNewTeamDialog = true;
                }"
              >
                <div class="flex items-center">
                  <Icon name="lucide:plus" class="mr-2 h-5 w-5" />
                  New Team
                </div>
              </CommandItem>
            </DialogTrigger>
          </CommandGroup>

          
        </CommandList>
      </Command>

      <DialogContent title="" :trapFocus="false"
        class="w-[80%] min-w-[80%] min-w-[90vw] sm:min-w-[85vw] dialog-shadow mt-0 pt-0 !border-0 gap-0 !rounded-2xl w-fit sm:max-w-[190vw] md:max-w-[190vw] lg:max-w-[80vw] transition-all duration-180 p-8 shadow-xxl">
        <ProTrial :team="true" v-if="currentTab == 'trial'" />
        <div v-else class="p-8">
          <div v-if="currentTab == 'use'" class="mb-5">
            <DialogTitle>
              
              <p class="text-2xl tracking-tight text-muted-foreground font-semibold">
                How do you want to use SuperSeeded?<br/>
                <span class="text-muted-foreground font-normal text-lg">This helps us customise your experience</span>
              </p>
            </DialogTitle>

            <div class="my-10">
              <RadioGroup default-value="option-one" class="flex justify-around space-x-1">
                <div v-for="option in useCases" :key="option.id"
                  class="justify-between cursor-pointer items-center w-full gap-3">
                  <RadioGroupItem :id="option.id" :value="option.id" class="hidden" />
                  <div :class="[
                    'aspect-square rounded-2xl masked flex items-center  w-full relative overflow-hidden group transition-all',
                    {
                      'shadow-xl hover:border-transparent':
                          selectedUseCase == option.id,
                    },
                  ]" @click="() => { selectedUseCase = option.id; }">
                    <div
                      :style="{
                        backgroundImage: `url('${option.image}')`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        opacity: selectedUseCase === option.id ? '1' : '0.5'
                      }"
                      class="absolute inset-0 bg-center bg-no-repeat transition-all ease-in-out transform scale-[1.1] group-hover:scale-[1.2] duration-1000"
                    ></div>
                    <div :class="['absolute inset-0 opacity-100  group-hover:opacity-80 transition-all', { 'bg-muted-foreground': selectedUseCase !== option.id }]"></div>
                    <Label :class="[
                      'cursor-pointer text-center w-full relative z-10 font-semibold text-lg transition-colors',
                      { 'text-white drop-shadow-lg': selectedUseCase == option.id },
                      'text-white'
                    ]" :for="option.id">{{ option.label }}</Label>
                      
                    </div>
                    
                  
                </div>
              </RadioGroup>
            </div>
          </div>

          <div v-if="currentTab == 'form'" class="mb-5">
            <DialogTitle>
              <p class="scroll-m-20 text-xl font-semibold tracking-tight">
                Create team
              </p>
              <p class="text-sm text-muted-foreground">
                Manage access to shared resources among team members.
              </p>
            </DialogTitle>

            <div class="space-y-4 pt-8 pb-4">
              <div class="space-y-2">
                <Label for="owner">Owner</Label>
                <TooltipProvider :delayDuration="0">
                  <Tooltip>
                    <TooltipTrigger><Input id="owner" disabled :placeholder="user?.display_name || user?.email" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        You are automatically team owner, as {{ user?.role }}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div class="space-y-2">
                <FormField v-slot="{ componentField }" name="name">
                  <FormItem v-auto-animate>
                    <FormLabel>Team Name</FormLabel>
                    <FormControl>
                      <Input type="text" placeholder="Team name" v-bind="componentField" />
                    </FormControl>
                    <FormMessage class="font-light" />
                  </FormItem>
                </FormField>
              </div>

              <div class="space-y-2">
                <FormField v-slot="{ componentField }" name="desc">
                  <FormItem v-auto-animate>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea v-bind="componentField" placeholder="Eg: Capital works L2, STMP, Estate 22 " />
                    </FormControl>
                    <FormMessage class="font-light" />
                  </FormItem>
                </FormField>
              </div>

              <!-- invites -->
              <div class="space-y-2">
                <FormField v-slot="{ componentField }" name="invitedUsers">
                  <FormItem v-auto-animate>
                    <FormLabel>Invite members</FormLabel>
                    <FormControl>
                      <TagsInput :delimiters="[32]" v-bind="componentField" :required="true" :addOnTab="true"
                        :addOnPaste="true" v-model="invitedUsers">
                        <TagsInputItem v-for="item in invitedUsers" :key="item" :value="item">
                          <TagsInputItemText />
                          <TagsInputItemDelete />
                        </TagsInputItem>
                        <TagsInputInput placeholder="Email addresses ← Tab/Enter to add more" />
                      </TagsInput>
                    </FormControl>
                    <FormMessage class="font-light" />
                  </FormItem>
                </FormField>
              </div>

              <!-- invite link -->

              <div class="space-y-2"></div>
            </div>
          </div>

          <div v-if="currentTab == 'invite'">
            <DialogTitle>
              <p class="scroll-m-20 text-xl font-semibold tracking-tight">
                Your team is ready.
              </p>
              <p class="text-md font-normal text-muted-foreground leading-7 [&:not(:first-child)]:mt-3">
                {{ invitedUsers.length > 0 ? 'Send out invites to your team members.' : 'Share these invite links with your team members.' }}
              </p>
            </DialogTitle>
            <div class="space-y-4 pt-8 pb-4">
              <div class="space-y-2">
                <div class="space-y-2 mt-">
                  <inviteLinks :invites="inviteUrls ? markRaw(inviteUrls) : null" />
                  <p class="text-sm text-muted-foreground">
                    {{ inviteUrls && inviteUrls.length > 0 ? `Share ${inviteUrls.length > 1 ? 'these' : 'this'} single-use invite link${
                    inviteUrls.length > 1 ? 's' : '' } with your team. One invitation per user.` : 'Getting those invites ready...' }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter v-if="currentTab != 'trial'" >
          <div class="p-8">
            <div v-if="currentTab == 'use'">
              <div class="space-x-2 flex justify-end">
                <Button variant="ghost" @click="showNewTeamDialog = false">
                  Cancel
                </Button>
                <Button :disabled="selectedUseCase == ''" v-if="true" @click.stop="currentTab = 'form'">
                  Next
                </Button>
                <Button v-else class="pl-3 subscribe whitespace-nowrap">
                  <Icon size="32" name="streamline:nature-ecology-rice-field-sun-rise-set-field-crop-produce-farm"
                    class="w-4 h-4 mr-4 -mt-1" />
                  <span class="text">Pro</span>
                  <span class="shimmer"></span>
                </Button>
              </div>
            </div>
            <div v-if="currentTab == 'form'" class="flex justify-end">
              <div class="space-x-2 flex">
                <Button variant="ghost" @click="currentTab = 'use'">
                  Back
                </Button>
                <Button @click="onSubmit" type="submit" :disabled="createTeamPending || !meta.valid">
                  <Loader2 v-if="createTeamPending" class="w-4 h-4 mr-2 animate-spin" />
                  Create
                </Button>
              </div>
            </div>

            <div v-if="currentTab == 'invite'" class="flex mt-6 space-x-2">
              <!-- skip goes to the team id from form values -->
              
              <!-- Show Skip/Go to team button when not sending or when no invites were created -->
              <Button v-if="sendingInvitations=='' || (!invitedUsers.length && !inviteUrls?.length)" variant="ghost" @click="goToTeam(submitCallback?.group_id)">
                {{ !invitedUsers.length && !inviteUrls?.length ? 'Go to team' : 'Skip' }}
                <Icon v-if="!invitedUsers.length && !inviteUrls?.length" name="ic:round-arrow-forward" class="ml-2 w-5 h-5" />
              </Button>
              
              <!-- Show send button if there are users to invite AND invite URLs are available -->
              <Button v-if="invitedUsers.length > 0 && inviteUrls?.length > 0" :disabled="!inviteUrl" @click="sendInvitations" :class="[{'pointer-events-none !cursor-default': sendingInvitations!=''}]">
                <Loader2 v-if="sendingInvitations == 'pending'" class="w-4 h-4 mr-2 animate-spin" />
                
                {{ sendingInvitations=='delivered' ? 'Sent' : sendingInvitations=='pending' ? 'Sending' : 'Send' }} {{ invitedUsers.length }} {{ invitedUsers.length > 1 ? 'invitations' : 'invitation' }}
                
                <Icon v-if="sendingInvitations=='sent'" name="ic:round-done" class="ml-2 w-5 h-5 text-muted-foreground" />
                <Icon v-if="sendingInvitations=='delivered'" name="material-symbols:done-all-rounded" class="ml-2 w-5 h-5 text-sky-400" />
              </Button>
              
              <!-- Show Go to team button after sending invitations -->
              <Button v-if="sendingInvitations=='sent' || sendingInvitations=='delivered'" variant="ghost" @click="goToTeam(submitCallback?.group_id)">
                Go to team <Icon name="ic:round-arrow-forward" class="ml-2 w-5 h-5" />
              </Button>
            
          </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </form>

  
</template>

<style>

.no-chevron svg{
  display:none;
}

.masked {
    mask-image: url("data:image/svg+xml,%3csvg width='200' height='200' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M100 0C20 0 0 20 0 100s20 100 100 100 100-20 100-100S180 0 100 0Z'/%3e%3c/svg%3e");
    mask-repeat: no-repeat;
    mask-size: 100%;
}



.shadow-inside {
  box-shadow:
    inset 0px -20px 20px -20px rgba(0, 0, 0, 0.2),
    inset 0px -10px 10px -10px rgba(0, 0, 0, 0.2);
}
</style>
